import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UncontrolledTooltip } from "reactstrap";
import { toast } from "react-toastify";
import { getAeCategoryWiseSlots } from "../../../../Api/apiUtils";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Badge,
  Field,
  TextInput,
  Toolbar,
  Table,
  Loader,
  EmptyState,
} from "../../../../ui";
import "./aeReports.scss";

/** Fixed 30-minute columns: 05:00 → 23:30 same day, then 00:00, 00:30, 01:00 (next morning). */
export function buildAeTimeSlotHeaders(): string[] {
  const keys: string[] = [];
  for (let h = 5; h <= 23; h++) {
    for (const minute of [0, 30]) {
      keys.push(
        `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`
      );
    }
  }
  keys.push("00:00:00", "00:30:00", "01:00:00");
  return keys;
}

const TIME_HEADERS = buildAeTimeSlotHeaders();

/** Valid DOM ids for reactstrap Tooltip (must be querySelector-safe; React useId() colons are not). */
const LEGEND_INTL_ID = "cw-ae-legend-intl";
const LEGEND_DOM_ID = "cw-ae-legend-dom";

/* International / domestic fills (accent-soft / warning-soft) live in aeReports.scss (.ae-slot--*),
   matching the legend badges. */

export type SlotCell = { startTime: string; isInternationalSlot: boolean };

type SlotDetail = {
  startTime?: string;
  isInternationalSlot?: boolean;
};

type TeacherRow = {
  teacherName?: string;
  teacherContact?: string;
  slotDetails?: SlotDetail[];
};

type CategoryBlock = {
  courseCategory?: string;
  teacherList?: TeacherRow[];
};

export type AeMatrixRow = {
  courseCategory: string;
  teacherName: string;
  mobileNumber: string;
  rowKey: string;
} & Record<string, SlotCell | string | undefined>;

export function normalizeAeTimeKey(raw: string): string {
  const s = raw.trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return s;
  const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const mm = Math.min(59, parseInt(m[2], 10));
  const ss = m[3] != null ? Math.min(59, parseInt(m[3], 10)) : 0;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function readAeSlotsPayload(raw: unknown): CategoryBlock[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as CategoryBlock[];
  if (typeof raw === "object" && raw !== null) {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data as CategoryBlock[];
    if (o.data && typeof o.data === "object") {
      const d = o.data as Record<string, unknown>;
      if (Array.isArray(d.content)) return d.content as CategoryBlock[];
    }
    if (Array.isArray(o.content)) return o.content as CategoryBlock[];
  }
  return [];
}

/** One matrix row per teacher; placeholder row if category has no teachers. */
export function flattenAeCategoryWiseRows(api: CategoryBlock[]): AeMatrixRow[] {
  const out: AeMatrixRow[] = [];
  let n = 0;
  for (const block of api) {
    const courseCategory = (block.courseCategory ?? "Unknown").trim() || "Unknown";
    const teachers = block.teacherList ?? [];
    if (teachers.length === 0) {
      const row: AeMatrixRow = {
        courseCategory,
        teacherName: "-",
        mobileNumber: "-",
        rowKey: `ph-${courseCategory}-${n++}`,
      };
      row["-"] = { startTime: "-", isInternationalSlot: false };
      out.push(row);
      continue;
    }
    for (const t of teachers) {
      const row: AeMatrixRow = {
        courseCategory,
        teacherName: (t.teacherName ?? "-").trim() || "-",
        mobileNumber: String(t.teacherContact ?? "-").trim() || "-",
        rowKey: `${courseCategory}-${t.teacherName ?? ""}-${n++}`,
      };
      const slots = t.slotDetails ?? [];
      if (slots.length === 0) {
        row["-"] = { startTime: "-", isInternationalSlot: false };
      } else {
        for (const s of slots) {
          if (!s.startTime) continue;
          const colKey = normalizeAeTimeKey(s.startTime);
          row[colKey] = {
            startTime: colKey,
            isInternationalSlot: Boolean(s.isInternationalSlot),
          };
        }
      }
      out.push(row);
    }
  }
  return out;
}

function renderSlotCell(value: unknown): React.ReactNode {
  if (value == null || typeof value !== "object") return null;
  const cell = value as SlotCell;
  if (cell.startTime === "-") return null;
  if (cell.isInternationalSlot === undefined) return null;
  return (
    <div
      className={`ae-slot ${cell.isInternationalSlot ? "ae-slot--intl" : "ae-slot--dom"}`}
      title={cell.isInternationalSlot ? "International" : "Domestic"}
    >
      {cell.startTime}
    </div>
  );
}

function exportMatrixCsv(rows: AeMatrixRow[], timeKeys: string[]): void {
  const headers = ["Course category", "Teacher", "Mobile", ...timeKeys];
  const esc = (v: string) => {
    const s = v.replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = rows.map((row) => {
    const cells = [
      row.courseCategory,
      row.teacherName,
      row.mobileNumber,
      ...timeKeys.map((tk) => {
        const v = row[tk];
        if (!v || typeof v !== "object") return "";
        const c = v as SlotCell;
        if (c.startTime === "-") return "";
        return c.startTime || "";
      }),
    ];
    return cells.map(esc).join(",");
  });
  const blob = new Blob([[headers.map(esc).join(","), ...lines].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "CategoryWiseAeDetails.csv";
  a.click();
  URL.revokeObjectURL(url);
}

const getTodayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const CategoryWiseAeReports: React.FC = () => {
  const initialDate = useMemo(() => getTodayYmd(), []);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AeMatrixRow[]>([]);

  const fetchForDate = useCallback(async (date: string) => {
    if (!date) {
      toast.error("Please select a date.");
      return;
    }
    setLoading(true);
    setData([]);
    try {
      const raw = await getAeCategoryWiseSlots(date);
      const blocks = readAeSlotsPayload(raw);
      setData(flattenAeCategoryWiseRows(blocks));
    } catch (e) {
      console.error(e);
      toast.error("Could not load AE slots for that date.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void fetchForDate(initialDate);
    }, 200);
    return () => window.clearTimeout(t);
  }, [initialDate, fetchForDate]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void fetchForDate(selectedDate);
  };

  const tableMinWidth = 280 + TIME_HEADERS.length * 56;

  return (
    <Page>
      <PageHeader
        eyebrow="AE Reports"
        title="CategoryWise AE Details"
        subtitle="For one calendar date: course category × teacher rows, 30-minute AE slots (international vs domestic)."
      />

      <Card>
        <form onSubmit={onSubmit}>
          <Toolbar>
            <Field label="Date" required>
              {(ctl) => (
                <TextInput
                  {...ctl}
                  type="date"
                  required
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  disabled={loading}
                />
              )}
            </Field>
            <div className="ae-reports__actions">
              <Button variant="primary" type="submit" loading={loading}>
                Submit
              </Button>
              <Button
                variant="secondary"
                disabled={loading || data.length === 0}
                onClick={() => exportMatrixCsv(data, TIME_HEADERS)}
              >
                Export to CSV
              </Button>
            </div>
          </Toolbar>
        </form>
      </Card>

      {!loading && data.length > 0 && (
        <div className="ae-reports__summary">
          <span className="ds-text-muted">Legend:</span>
          <span id={LEGEND_INTL_ID}>
            <Badge tone="accent">International</Badge>
          </span>
          <UncontrolledTooltip placement="top" target={LEGEND_INTL_ID}>
            International slot (pink)
          </UncontrolledTooltip>
          <span id={LEGEND_DOM_ID}>
            <Badge tone="warning">Domestic</Badge>
          </span>
          <UncontrolledTooltip placement="top" target={LEGEND_DOM_ID}>
            Domestic slot (amber)
          </UncontrolledTooltip>
        </div>
      )}

      {loading ? (
        <Loader label="Loading AE slots…" />
      ) : data.length === 0 ? (
        <EmptyState title="No data found for this date." description="Pick another date, then select Submit." />
      ) : (
        <Card flush className="ae-reports__scroll ae-reports__scroll--70">
          {/* minWidth is data-driven from the number of 30-minute columns. */}
          <Table
            caption="AE slots by category and teacher"
            className="ae-reports__grid ae-matrix"
            style={{ minWidth: tableMinWidth }}
          >
            <thead>
              <tr>
                <th className="ae-reports__sticky-col ae-reports__col-fixed">Category</th>
                <th className="ae-reports__sticky-col ae-reports__sticky-col--2 ae-reports__col-fixed">Teacher</th>
                {TIME_HEADERS.map((tk) => (
                  <th key={tk} className="ae-matrix__time">
                    {tk.slice(0, 5)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.rowKey}>
                  <td className="ae-reports__sticky-col ae-reports__col-fixed">{row.courseCategory}</td>
                  <td className="ae-reports__sticky-col ae-reports__sticky-col--2 ae-reports__col-fixed">
                    {row.teacherName}
                  </td>
                  {TIME_HEADERS.map((tk) => (
                    <td key={`${row.rowKey}-${tk}`} className="ae-matrix__cell">
                      {renderSlotCell(row[tk])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </Page>
  );
};

export default CategoryWiseAeReports;
